import { useState } from 'react';
import { ArrowPathIcon, ClockIcon, CubeIcon, InformationCircleIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { ConfrimModal } from '../ConfrimModal';
import ForecastDemandProductsDialog from './ForecastDemandProductsDialog';
import { DemandPlanningHistorySidebar } from './DemandPlanningHistorySidebar';

function DemandPlanningHeaderToolbar({
  activePreset,
  isForecastDemandTable,
  onOpenGovernanceDialog,
  onOpenColumnsDrawer,
  onRefreshSalesSnapshot,
  isRefreshingSales = false,
  demandPlanningId,
  demandPlanningYear,
  showAdditionalButtons = false,
  hideColumnsButtonOnMobile = false,
  hideGovernanceButtonOnMobile = false,
  withCustomColumns = false,
  marginFromSidebar,
  drawerWidth,
}) {
  const [refreshSalesConfirmOpen, setRefreshSalesConfirmOpen] = useState(false);
  const [forecastProductsDialogOpen, setForecastProductsDialogOpen] = useState(false);
  const [historySidebarOpen, setHistorySidebarOpen] = useState(false);
  return (
    <div className="flex w-full flex-col flex-wrap items-initial justify-end gap-2 sm:w-auto sm:flex-row sm:items-center">
      <div className="flex flex-row items-center gap-2">
        {isForecastDemandTable && (
          <>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white p-2 text-sm text-gray-900 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:w-auto"
              onClick={() => setForecastProductsDialogOpen(true)}
              disabled={!demandPlanningId || demandPlanningYear == null || demandPlanningYear === ''}
              title="View forecast demand products"
              aria-label="View forecast demand products"
            >
              <CubeIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white p-2 text-sm text-gray-900 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:w-auto"
              onClick={() => setRefreshSalesConfirmOpen(true)}
              disabled={!onRefreshSalesSnapshot || isRefreshingSales}
              title="Refresh sales snapshot"
              aria-label="Refresh sales snapshot"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isRefreshingSales ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white p-2 text-sm text-gray-900 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:w-auto"
              onClick={() => setHistorySidebarOpen(true)}
              disabled={!demandPlanningId || demandPlanningYear == null || demandPlanningYear === ''}
              title="View change log"
              aria-label="View change log"
            >
              <ClockIcon className="h-4 w-4" />
            </button>
          </>
        )}
        {withCustomColumns && (
          <span className="text-xs font-medium text-gray-600">
            Custom
          </span>
        )}
        <button
          className={`rounded-full border border-gray-300 border-border bg-white p-2 shadow-sm transition hover:bg-gray-50 ${hideGovernanceButtonOnMobile ? 'hidden md:block' : ''}`}
          onClick={() => {
            onOpenGovernanceDialog?.(activePreset?.id);
          }}
          type="button"
        >
          <InformationCircleIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {showAdditionalButtons && (
        <button
          type="button"
          className={`h-auto w-full items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-0.5 text-sm text-gray-900 shadow-sm transition hover:bg-gray-50 sm:h-9 sm:w-auto ${hideColumnsButtonOnMobile ? 'hidden md:inline-flex' : 'inline-flex'}`}
          onClick={() => onOpenColumnsDrawer?.()}
        >
          <ViewColumnsIcon className="h-4 w-4" />
          Columns
        </button>
      )}
      <DemandPlanningHistorySidebar
        isOpen={historySidebarOpen}
        demandPlanningId={demandPlanningId}
        demandPlanningYear={demandPlanningYear}
        onClose={() => setHistorySidebarOpen(false)}
      />
      <ForecastDemandProductsDialog
        open={forecastProductsDialogOpen}
        onOpenChange={setForecastProductsDialogOpen}
        demandPlanningId={demandPlanningId}
        demandPlanningYear={demandPlanningYear}
        marginFromSidebar={marginFromSidebar}
        drawerWidth={drawerWidth}
      />
      <ConfrimModal
        isOpen={refreshSalesConfirmOpen}
        isLoading={isRefreshingSales}
        title="Are you sure you want to update sales for the current year by products and categories?"
        confirmButtonTitle="Yes"
        submitClick={async () => {
          if (!onRefreshSalesSnapshot) return;
          try {
            await onRefreshSalesSnapshot();
            setRefreshSalesConfirmOpen(false);
          } catch {
            // Parent handles toast/error; keep modal open for retry.
          }
        }}
        cancelClick={() => {
          if (isRefreshingSales) return;
          setRefreshSalesConfirmOpen(false);
        }}
        size="max-w-lg"
        testId="demand-planning-refresh-sales-snapshot"
      />
    </div>
  );
}

export default DemandPlanningHeaderToolbar;
